import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { SeriesService } from '../../services/series';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { IndDatePipe } from '../../pipes/ind-date';

@Component({
  selector: 'app-series-types',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    TableModule,
    TooltipModule,
    InputTextModule,
    IndDatePipe,
  ],
  templateUrl: './series-types.html',
  styleUrls: ['./series-types.css'],
})
export class SeriesTypes implements OnInit {
  private svc = inject(SeriesService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  /** Page heading — pulled from the menu so it always matches the clicked item. */
  readonly pageTitle = inject(PageTitleService).title;

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  types = signal<any[]>([]);

  editId = signal<number>(0);
  name = signal('');

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.getAllTypes().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.types.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  openAdd() {
    this.editing.set(false);
    this.editId.set(0);
    this.name.set('');
    this.showModal.set(true);
  }

  openEdit(t: any) {
    this.editing.set(true);
    this.editId.set(t.id ?? t.seriesTypeId ?? t.seriesTypeID);
    this.name.set(t.type ?? t.name ?? t.seriesType ?? t.typeName ?? t.seriesTypeName ?? '');
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    const value = this.name().trim();
    if (!value) {
      this.toast.warning('Type name is required');
      return;
    }
    this.saving.set(true);
    const req = this.editing()
      ? this.svc.updateType(this.editId(), value)
      : this.svc.insertType(value);

    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save type');
          return;
        }
        this.toast.success(this.editing() ? 'Series type updated' : 'Series type created');
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  async remove(t: any) {
    const id = t.id ?? t.seriesTypeId ?? t.seriesTypeID;
    const label = t.type ?? t.name ?? t.seriesType ?? t.typeName ?? t.seriesTypeName ?? 'this type';
    const ok = await this.toast.confirm(`"${label}" will be deleted.`, 'Delete series type?', 'Delete');
    if (!ok) return;
    this.svc.deleteType(id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Series type deleted');
        this.load();
      },
    });
  }

  displayName(t: any) {
    return t.type ?? t.name ?? t.seriesType ?? t.typeName ?? t.seriesTypeName ?? '—';
  }

  displayId(t: any) {
    return t.id ?? t.seriesTypeId ?? t.seriesTypeID ?? '—';
  }
}
